import { useState } from "react";

import { sunAltitudeAt, twilightTimes } from "@nav-games/astro";

/// Twilight calculator — pick a date and position, see sunrise/sunset
/// plus the three twilight phases at that latitude in UT. Particularly
/// useful for planning star sights: the best window is **nautical
/// twilight**, when the brightest stars are visible *and* the horizon
/// is still sharp enough to bring them down.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function fmtUT(date: Date | null): string {
  if (!date) return "—";
  const hh = String(date.getUTCHours()).padStart(2, "0");
  const mm = String(date.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm} UT`;
}

function todayIso(): string {
  const now = new Date();
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function TwilightCalculator() {
  const [dateStr, setDateStr] = useState<string>(todayIso());
  const [lat, setLat] = useState("51.5");
  const [lon, setLon] = useState("-0.1");

  const valid =
    /^\d{4}-\d{2}-\d{2}$/.test(dateStr) &&
    Number.isFinite(Number(lat)) &&
    Number.isFinite(Number(lon));

  const t = valid
    ? twilightTimes(new Date(`${dateStr}T12:00:00Z`), Number(lat), Number(lon))
    : null;

  const rows: ReadonlyArray<[string, number, Date | null, Date | null]> = t
    ? [
        ["Astronomical twilight", -18, t.astronomicalDawn, t.astronomicalDusk],
        ["Nautical twilight", -12, t.nauticalDawn, t.nauticalDusk],
        ["Civil twilight", -6, t.civilDawn, t.civilDusk],
        ["Sunrise / sunset", -0.833, t.sunrise, t.sunset],
      ]
    : [];

  // Verification: the Sun's altitude *at the computed event time*
  // should equal the threshold to within a hundredth of a degree.
  const verify = (d: Date | null): string => {
    if (!d || !valid) return "—";
    const alt = sunAltitudeAt(d, Number(lat), Number(lon));
    return `${alt.toFixed(2)}°`;
  };

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Twilight finder</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Computed from the same Sun ephemeris as the calculator above —
        accurate to a minute or two for any latitude up to the polar
        circles, plenty for planning a morning or evening sight.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 140 }}>
          <span className="small muted">Date (UTC)</span>
          <input
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            style={inputStyle}
            aria-label="Date"
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 120 }}>
          <span className="small muted">Latitude (° N+/S−)</span>
          <input
            type="number"
            inputMode="decimal"
            step={0.1}
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            style={inputStyle}
            aria-label="Latitude"
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 120 }}>
          <span className="small muted">Longitude (° E+/W−)</span>
          <input
            type="number"
            inputMode="decimal"
            step={0.1}
            value={lon}
            onChange={(e) => setLon(e.target.value)}
            style={inputStyle}
            aria-label="Longitude"
          />
        </label>
      </div>

      {t ? (
        <>
          <table style={{ width: "100%", fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "4px 8px" }}>Phase</th>
                <th style={{ textAlign: "right", padding: "4px 8px" }}>Altitude</th>
                <th style={{ textAlign: "left", padding: "4px 8px" }}>Dawn (verify h)</th>
                <th style={{ textAlign: "left", padding: "4px 8px" }}>Dusk (verify h)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, threshold, dawn, dusk]) => (
                <tr key={label}>
                  <td style={{ padding: "4px 8px", fontWeight: 600 }}>{label}</td>
                  <td className="muted" style={{ padding: "4px 8px", textAlign: "right" }}>
                    {threshold.toFixed(2)}°
                  </td>
                  <td className="muted" style={{ padding: "4px 8px" }}>
                    {fmtUT(dawn)} <span style={{ opacity: 0.7 }}>({verify(dawn)})</span>
                  </td>
                  <td className="muted" style={{ padding: "4px 8px" }}>
                    {fmtUT(dusk)} <span style={{ opacity: 0.7 }}>({verify(dusk)})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div
            className="small"
            style={{
              marginTop: 12,
              padding: 12,
              background: "var(--bg-elev)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
            {`Method
─────────────────────────────────────────────
At any instant the Sun's altitude h is found from

  sin(h) = sin(Lat)·sin(Dec) + cos(Lat)·cos(Dec)·cos(LHA)

where LHA = GHA_Sun + λ_E (mod 360°). GHA_Sun and Dec
come from the Sun ephemeris (the "Sun right now" panel
below shows the values for the current instant).

The four "altitude" thresholds above are the moments
the Sun's centre crosses −0.83° / −6° / −12° / −18°.
Each "(verify h)" cell shows the altitude computed at
the event time — it should equal the threshold, the
quick check that the search converged.`}
          </div>
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter a valid date and position.
        </p>
      )}

      <p className="small muted" style={{ marginTop: 12, marginBottom: 0 }}>
        For star sights aim for <strong>nautical twilight</strong> —
        bright stars are visible <em>and</em> the horizon is still sharp.
      </p>
    </div>
  );
}
